/**
 * Create Role Modal - Extracted from admin.tsx per ADR-039
 * Modal for creating new custom roles with permissions
 */

import type { PermissionResponse } from "@/types/models"
import {
  Box,
  Flex,
  Input,
  Stack,
  Text,
  Textarea,
  createToaster,
} from "@chakra-ui/react"
import type React from "react"
import { useState } from "react"

import { Button } from "@/components/atoms/Button"
import { Checkbox } from "@/components/atoms/Checkbox"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
} from "@chakra-ui/react"

export function CreateRoleModal({
  isOpen,
  onClose,
  permissions,
  onCreateRole,
}: {
  isOpen: boolean
  onClose: () => void
  permissions: PermissionResponse[]
  onCreateRole: (data: {
    name: string
    display_name: string
    description?: string
    role_type: string
    permission_ids?: string[]
  }) => Promise<any>
}) {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    permission_ids: [] as string[],
  })
  const toaster = createToaster({
    placement: "top-end",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await onCreateRole({
        ...formData,
        role_type: "CUSTOM",
      })
      toaster.create({
        title: "Role created successfully",
        type: "success",
        duration: 3000,
      })
      onClose()
      setFormData({
        name: "",
        display_name: "",
        description: "",
        permission_ids: [],
      })
    } catch (error) {
      toaster.create({
        title: "Failed to create role",
        type: "error",
        duration: 3000,
      })
    }
  }

  const groupedPermissions = (permissions || []).reduce(
    (
      acc: Record<string, PermissionResponse[]>,
      permission: PermissionResponse,
    ) => {
      const resourceType = permission.resource_type || "general"
      if (!acc[resourceType]) {
        acc[resourceType] = []
      }
      acc[resourceType].push(permission)
      return acc
    },
    {},
  )

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size={{ base: "full", md: "xl" }}
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner
          inset="0"
          display="grid"
          placeItems="center"
          padding={{ base: 4, md: 6 }}
        >
          <DialogContent maxH="90vh" display="flex" flexDirection="column">
            <DialogHeader>
              <DialogTitle>Create New Role</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>
            <DialogBody pb={6} overflowY="auto" flex="1">
              <form onSubmit={handleSubmit}>
                <Stack gap={4} align="stretch">
                  <Flex>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        Role Name
                      </Text>
                      <Input
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="e.g. content-moderator"
                        required
                      />
                    </Box>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        Display Name
                      </Text>
                      <Input
                        value={formData.display_name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            display_name: e.target.value,
                          }))
                        }
                        placeholder="e.g. Content Moderator"
                        required
                      />
                    </Box>
                  </Flex>

                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Description
                    </Text>
                    <Textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Describe what this role can do..."
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Permissions
                    </Text>
                    <Box
                      maxH="300px"
                      overflowY="auto"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={4}
                    >
                      {Object.entries(groupedPermissions).map(
                        ([resourceType, resourcePermissions]: [
                          string,
                          any,
                        ]) => (
                          <Box key={resourceType} mb={4}>
                            <Text
                              fontWeight="medium"
                              mb={2}
                              textTransform="capitalize"
                            >
                              {resourceType} ({resourcePermissions.length})
                            </Text>
                            <Stack align="start" gap={2} ml={4}>
                              {resourcePermissions.map(
                                (permission: PermissionResponse) => (
                                  <Checkbox
                                    key={permission.id}
                                    checked={formData.permission_ids.includes(
                                      permission.id,
                                    )}
                                    onCheckedChange={(details) => {
                                      const isChecked = details.checked
                                      if (isChecked) {
                                        setFormData((prev) => ({
                                          ...prev,
                                          permission_ids: [
                                            ...prev.permission_ids,
                                            permission.id,
                                          ],
                                        }))
                                      } else {
                                        setFormData((prev) => ({
                                          ...prev,
                                          permission_ids:
                                            prev.permission_ids.filter(
                                              (id) => id !== permission.id,
                                            ),
                                        }))
                                      }
                                    }}
                                  >
                                    <Stack align="start" gap={1}>
                                      <Text fontSize="sm">
                                        {permission.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {permission.description}
                                      </Text>
                                    </Stack>
                                  </Checkbox>
                                ),
                              )}
                            </Stack>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Box>

                  <Flex justify="end">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" colorScheme="blue">
                      Create Role
                    </Button>
                  </Flex>
                </Stack>
              </form>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  )
}
